import { useEffect, useRef, useState } from 'react';

import { anioDePeriodo, importe, nombreDePeriodo } from '../formato.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

/*
 * LA PREGUNTA DE PROPAGACIÓN · UNA SOLA POR GUARDADO (RF-111, RN-230, RN-232).
 *
 * ES OPT-IN Y ESTO ES CONTRATO, NO ESTILO (D-155):
 *   · «Solo este mes» ES LA ACCIÓN POR DEFECTO: tiene el foco al abrir, y es lo que
 *     ocurre al cerrar la ventana o al pulsar Escape. EL SILENCIO NUNCA PROPAGA.
 *   · No hay preferencia guardada, ni casilla de «recordar mi respuesta», ni ajuste
 *     que la vuelva automática.
 *   · Se escribe EL MISMO IMPORTE, no la diferencia (RN-230d), y solo en meses
 *     POSTERIORES DEL MISMO AÑO: el año siguiente nunca se toca (RN-230c).
 *
 * CUÁNDO APARECE LO DECIDE EL SERVIDOR (D-151, RN-230b): esta pieza se monta solo
 * si el guardado devolvió `categoriasConImporteCambiado` y `mesesSiguientesDelAnio`
 * mayor que cero. El mes YA QUEDÓ GUARDADO y no depende de lo que se conteste
 * (RN-230i): quitar un presupuesto tampoco pregunta, porque quitar no es cambiar
 * de importe.
 */
export function DialogoDePropagacion({
  idPeriodo,
  categoriasConImporteCambiado,
  mesesSiguientesDelAnio,
  alSoloEsteMes,
  alPropagar,
  procesando = false,
  error = null,
}) {
  const botonPorDefecto = useRef(null);
  const [marcadas, setMarcadas] = useState(
    () => new Set(categoriasConImporteCambiado.map((c) => c.idCategoria)),
  );

  // EL FOCO ARRANCA EN «SOLO ESTE MES»: es la acción por defecto (RN-232).
  useEffect(() => { botonPorDefecto.current?.focus(); }, []);

  // ESCAPE EQUIVALE A NO PROPAGAR, igual que cerrar (RN-230e).
  useEffect(() => {
    function alPulsar(evento) {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        alSoloEsteMes();
      }
    }
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [alSoloEsteMes]);

  function alternar(idCategoria) {
    setMarcadas((previas) => {
      const copia = new Set(previas);
      if (copia.has(idCategoria)) copia.delete(idCategoria); else copia.add(idCategoria);
      return copia;
    });
  }

  const anio = anioDePeriodo(idPeriodo);

  return (
    <div
      className="card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tituloDePropagacion"
      style={{ margin: 12 }}
      data-prueba="pregunta-de-propagacion"
    >
      <b id="tituloDePropagacion">¿Aplicar el cambio a los meses siguientes de {anio}?</b>

      <span className="ayuda">
        <b>{nombreDePeriodo(idPeriodo)} ya quedó guardado.</b> Esto es aparte y solo pasa si lo
        pides: cambiaste el importe de {categoriasConImporteCambiado.length}{' '}
        categoría{categoriasConImporteCambiado.length === 1 ? '' : 's'} y quedan{' '}
        {mesesSiguientesDelAnio} mes{mesesSiguientesDelAnio === 1 ? '' : 'es'} por delante en {anio}.
      </span>

      {categoriasConImporteCambiado.map((categoria) => (
        <label className="fila" key={categoria.idCategoria} data-prueba="categoria-a-propagar">
          <input
            type="checkbox"
            checked={marcadas.has(categoria.idCategoria)}
            onChange={() => alternar(categoria.idCategoria)}
            aria-label={`Propagar ${categoria.nombreCategoria}`}
          />
          <span className="crece">
            <b>{categoria.nombreCategoria}</b>
            <small style={{ display: 'block' }}>
              quedará en {importe(categoria.importeAsignado)} en los meses siguientes de {anio}
            </small>
          </span>
        </label>
      ))}

      <span className="ayuda">
        Se aplica <b>el mismo importe</b>, no la diferencia. Lo habitual es <b>solo este mes</b>:
        subir o bajar un mes casi siempre responde a algo que pasa <b>en ese mes</b> —los libros de
        mayo, el inicio de clases de agosto, la doble colegiatura de diciembre—. Cerrar esta
        ventana o pulsar <b>Escape</b> equivale a «solo este mes». <b>{anio + 1} no cambia.</b>
      </span>

      <AvisoDeError error={error} datoDePrueba="error-de-propagacion" />

      <div className="fila">
        <button
          className="btn"
          type="button"
          ref={botonPorDefecto}
          disabled={procesando}
          onClick={alSoloEsteMes}
          data-prueba="solo-este-mes"
        >
          Solo este mes
        </button>
        <button
          className="btn sec"
          type="button"
          disabled={procesando || marcadas.size === 0}
          onClick={() => alPropagar(
            categoriasConImporteCambiado
              .filter((categoria) => marcadas.has(categoria.idCategoria))
              .map((categoria) => ({
                idCategoria: categoria.idCategoria,
                // EL MISMO IMPORTE que quedó en este mes (RN-230d).
                importeAsignado: categoria.importeAsignado,
              })),
          )}
          data-prueba="aplicar-a-meses-siguientes"
        >
          Aplicar a los meses siguientes
        </button>
      </div>
    </div>
  );
}
